import { EntitySetting } from "@fairfleet/geotab";
import { TachographPolicy } from "../models/Tachograph";

/** EU default: a driver card has to be read out every 28 days. */
export const DEFAULT_DRIVER_INTERVAL_DAYS = 28;

/** EU default: a vehicle unit has to be read out every 90 days. */
export const DEFAULT_DEVICE_INTERVAL_DAYS = 90;

/** Geotab keeps the tachograph settings of the database under this key. */
const COMPANY_SETTINGS_KEY = "dtco.companySettings";

const MAX_INTERVAL_DAYS = 3650;

type CompanySettings = {
  driverManualDownloadPeriod?: unknown;
  vehicleManualDownloadPeriod?: unknown;
};

function toIntervalDays(value: unknown): number | null {
  const days = typeof value === "number" ? value : Number(value);

  if (!Number.isFinite(days) || days <= 0 || days > MAX_INTERVAL_DAYS) return null;

  return days;
}

function parseCompanySettings(settings: Array<Partial<EntitySetting>>): CompanySettings | null {
  const raw = settings.find(setting => setting.key === COMPANY_SETTINGS_KEY)?.settingValue;
  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);

    return parsed && typeof parsed === "object" ? (parsed as CompanySettings) : null;
  } catch {
    return null;
  }
}

/** Reads the readout intervals from the database settings, falling back to the EU defaults. */
export function createTachographPolicy(settings: Array<Partial<EntitySetting>>): TachographPolicy {
  const companySettings = parseCompanySettings(settings);
  const driverIntervalDays = toIntervalDays(companySettings?.driverManualDownloadPeriod);
  const deviceIntervalDays = toIntervalDays(companySettings?.vehicleManualDownloadPeriod);

  return {
    driverIntervalDays: driverIntervalDays ?? DEFAULT_DRIVER_INTERVAL_DAYS,
    deviceIntervalDays: deviceIntervalDays ?? DEFAULT_DEVICE_INTERVAL_DAYS,
    source: driverIntervalDays !== null || deviceIntervalDays !== null ? "geotab" : "fallback",
  };
}
